import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ShieldCheck, ChevronLeft, Phone, Mail, MapPin, Facebook } from "lucide-react";
import { api, fileUrl, formatError } from "@/lib/api";
import { LogoMark } from "@/components/LogoMark";
import { StoreFooter } from "@/components/StoreFooter";

function StorefrontHeader() {
  return (
    <>
      <div className="gov-strip">
        <span className="tri"><span className="b saffron" /><span className="b white" /><span className="b green" /></span>
        <Link to="/about">About us</Link>
        <Link to="/authorised-users">Authorised users</Link>
        <Link to="/contact">Contact</Link>
      </div>
      <header className="store-header">
        <Link to="/" className="store-brand"><LogoMark small /><span>GI Bharat<small>By True Vision Welfare Society</small></span></Link>
        <nav><Link to="/login" className="store-login">Sign in</Link></nav>
      </header>
    </>
  );
}

export default function SellerProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/sellers/${id}`).then(r => setData(r.data)).catch(err => setError(formatError(err)));
  }, [id]);

  if (error) return <main className="storefront"><StorefrontHeader /><div className="pd-shell"><Link to="/" className="pd-back"><ChevronLeft size={14} /> Back</Link><div className="error" data-testid="sp-error">{error}</div></div></main>;
  if (!data) return <main className="storefront"><StorefrontHeader /><div className="pd-shell" data-testid="sp-loading">Loading seller…</div></main>;

  const { seller, profile, products } = data;
  const p = profile || {};
  return (
    <main className="storefront" data-testid="seller-profile-page">
      <StorefrontHeader />

      <div className="pd-shell">
        <Link to="/" className="pd-back"><ChevronLeft size={14} /> Back to marketplace</Link>

        <section style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 26, background: "#fff", border: "1px solid var(--line)", borderRadius: 8, padding: 26, marginBottom: 26 }}>
          <div style={{ width: 180, height: 180, borderRadius: "50%", background: "var(--soft)", overflow: "hidden", display: "grid", placeItems: "center", color: "var(--saffron)", border: "1px solid var(--line)" }}>
            {p.profile_photo ? <img src={fileUrl(p.profile_photo)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "✦"}
          </div>
          <div>
            <p className="eyebrow">GI AUTHORISED MAKER</p>
            <h1 style={{ color: "var(--tv-green)", marginBottom: 4 }} data-testid="sp-name">{p.firm_name || seller.name}</h1>
            {p.proprietor_name && <p className="muted" style={{ margin: 0 }}>{p.proprietor_name}</p>}
            {p.gi_item_name && <p style={{ color: "var(--saffron-dark)", fontWeight: 700, margin: "8px 0 0" }}>{p.gi_item_name}</p>}
            {seller.verified
              ? <span className="verified" style={{ marginTop: 10 }}><ShieldCheck size={13} /> Verified authorised user</span>
              : <span className="verified pending" style={{ marginTop: 10 }}><ShieldCheck size={13} /> Pending verification</span>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px 20px", marginTop: 14, color: "var(--muted)", fontSize: 13 }} data-testid="sp-details">
              {p.gi_application_no && <span><b style={{ color: "var(--ink)" }}>GI Application No.</b><br />{p.gi_application_no}</span>}
              {p.item_name && <span><b style={{ color: "var(--ink)" }}>Item</b><br />{p.item_name}</span>}
              {p.gst_number && <span><b style={{ color: "var(--ink)" }}>GSTIN</b><br />{p.gst_number}</span>}
              {p.pan_number && <span><b style={{ color: "var(--ink)" }}>PAN</b><br />{p.pan_number}</span>}
              {p.msme_number && <span><b style={{ color: "var(--ink)" }}>MSME</b><br />{p.msme_number}</span>}
              {p.profile_type && <span><b style={{ color: "var(--ink)" }}>Type</b><br />{p.profile_type}</span>}
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12, color: "var(--muted)", fontSize: 13 }}>
              {p.address && <span><MapPin size={13} /> {p.address}</span>}
              {seller.phone && <span><Phone size={13} /> {seller.phone}</span>}
              {seller.email && <span><Mail size={13} /> {seller.email}</span>}
              {p.facebook_url && <a href={p.facebook_url} target="_blank" rel="noreferrer" style={{ color: "var(--tv-green)" }}><Facebook size={13} /> Facebook</a>}
            </div>
          </div>
        </section>

        {p.history && (
          <section style={{ marginBottom: 24 }} data-testid="sp-history">
            <h3 style={{ color: "var(--tv-green)" }}>Our story</h3>
            <p className="muted" style={{ lineHeight: 1.7 }}>{p.history}</p>
          </section>
        )}

        {p.achievements?.length > 0 && (
          <section style={{ marginBottom: 24 }} data-testid="sp-achievements">
            <h3 style={{ color: "var(--tv-green)" }}>Achievements & awards</h3>
            <div className="store-grid" style={{ marginTop: 12 }}>
              {p.achievements.map((a, i) => (
                <div className="admin-tile" key={i} data-testid={`sp-ach-${i}`}>
                  <h4>{a.title}</h4>
                  {a.date && <p className="eyebrow" style={{ margin: 0 }}>{a.date}</p>}
                  <p style={{ color: "var(--muted)", fontSize: 13 }}>{a.description}</p>
                  {a.photos?.length > 0 && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: 6, marginTop: 8 }}>
                      {a.photos.map((ph, j) => <img key={j} src={fileUrl(ph)} alt="" style={{ width: "100%", aspectRatio: 1, objectFit: "cover", borderRadius: 4 }} />)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <section data-testid="sp-products">
          <h3 style={{ color: "var(--tv-green)" }}>Products by this maker</h3>
          {products?.length ? (
            <div className="store-grid" style={{ marginTop: 12 }}>
              {products.map(prod => (
                <Link to={`/product/${prod.id}`} className="product-card" key={prod.id} data-testid={`sp-product-${prod.id}`}>
                  <div className="product-art">
                    {prod.images?.[0] ? <img src={fileUrl(prod.images[0])} alt="" /> : <span className="placeholder">✦</span>}
                  </div>
                  <div className="product-body">
                    <h4>{prod.name}</h4>
                    <p>{prod.gi_name}</p>
                    <div className="price-row"><span className="now">₹{Number(prod.final_price).toLocaleString("en-IN")}</span></div>
                  </div>
                </Link>
              ))}
            </div>
          ) : <div className="empty-state" data-testid="sp-no-products"><p>No products listed yet.</p></div>}
        </section>
      </div>

      <StoreFooter />
    </main>
  );
}
